import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import TabStrip from "../components/TabStrip.jsx";
import { ScopeLabel } from "../components/AdminViewHeader.jsx";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { NOTE_CLASS } from "../lib/ui.js";
import { AiServicesSection, CustomProvidersSection } from "./SettingsPage.jsx";

const GOVERNANCE_TABS = [
    {
        id: "services",
        label: "Sanctioned AI",
        description: "Decide which recognized AI services are approved, unapproved, or excluded from organization-wide AI service views and analytics.",
        permission: PERMISSIONS.TENANT_CONFIG_READ,
    },
    {
        id: "detection",
        label: "Detection Rules",
        description: "Define how Origin recognizes additional AI services from observed provider hostnames.",
        permission: PERMISSIONS.TENANT_CONFIG_WRITE,
    },
];

export default function AiServicesAdminPage({ viewDef }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const caps = useMyPermissions();
    const canWrite = hasPermission(caps, PERMISSIONS.TENANT_CONFIG_WRITE);
    const visibleTabs = useMemo(
        () => GOVERNANCE_TABS.filter((tab) => hasPermission(caps, tab.permission)),
        [caps],
    );
    const requestedTab = searchParams.get("governanceTab");
    const activeTab = visibleTabs.find((tab) => tab.id === requestedTab) ?? visibleTabs[0];

    useEffect(() => {
        if (!requestedTab || !activeTab || requestedTab === activeTab.id) return;
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (activeTab.id === "services") next.delete("governanceTab");
            else next.set("governanceTab", activeTab.id);
            return next;
        }, { replace: true });
    }, [activeTab, requestedTab, setSearchParams]);

    // Canonicalize old query-param bookmarks now that this workspace is no
    // longer an Admin child. App routes the legacy URL here synchronously so
    // there is no flash of the Access page; this replaces only the stale mode.
    useEffect(() => {
        if (searchParams.get("mode") !== "admin") return;
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            next.set("mode", "governance");
            return next;
        }, { replace: true });
    }, [searchParams, setSearchParams]);

    const selectTab = (id) => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (id === "services") next.delete("governanceTab");
            else next.set("governanceTab", id);
            return next;
        });
    };

    const header = (
        <TabStrip
            tabs={visibleTabs.map((tab) => ({
                id: tab.id,
                label: tab.label,
                active: tab.id === activeTab?.id,
                dot: tab.id === activeTab?.id ? "bg-[color:var(--green)]" : undefined,
                onSelect: () => selectTab(tab.id),
            }))}
            actions={<ScopeLabel>Organization-wide</ScopeLabel>}
            className="pt-[11px] max-[900px]:pt-[8px]"
        />
    );

    return (
        <>
            <header className="shrink-0 overflow-hidden" aria-label={viewDef?.label ?? "AI Governance"}>
                {header}
            </header>
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto py-[12px]">
                <div className="grid gap-[14px]">
                    <header className="grid gap-[3px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[12px] py-[10px]">
                        <p className={`${NOTE_CLASS} text-[11px] leading-[1.4]`}>
                            {activeTab?.description}
                        </p>
                    </header>
                    {activeTab?.id === "detection"
                        ? <CustomProvidersSection />
                        : <AiServicesSection canWrite={canWrite} collapsible={false} />}
                </div>
            </div>
        </>
    );
}
